import { useEffect, useRef, useState } from 'react'
import ActorPicker from '../ActorPicker'
import { profileUrl } from '../../api/tmdb'
import { updateParty } from '../../utils/partyGame'
import { DCDirectorBanner, DCHostDirectorLine } from './PartyDCIdentity'
import { IconCheck } from '../icons'

const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"

/**
 * Director's Cut — Phase 2: Casting Session.
 *
 * dcStep substatus:
 *   'collect'    : agents nominate TWO actors each (anonymous), written as
 *                  castingNominations.{uid}_1 / _2. Director sees who-submitted.
 *   'pick-start' : director picks START from anonymous grid. +2 to nominating agent.
 *   'pick-goal'  : director picks GOAL from remaining nominations. +2 to nominating agent.
 *   'done'       : transitions to cinematic reveal (status='reveal').
 *
 * role: 'host' | 'director' | 'agent'
 */
export default function PartyDCCasting({ party, currentUserId, role }) {
  const players = party?.players ?? {}
  const directorUid = party?.directorUid
  const agentUids = Object.keys(players).filter(uid => uid !== directorUid)
  const nominations = party?.castingNominations ?? {}
  // Each agent nominates TWO actors, written as castingNominations.{uid}_1 and
  // castingNominations.{uid}_2 — each value stays an actor map (matches the shape
  // the tvOS host + dedup already expect). An agent counts as submitted once both
  // keys exist.
  const hasBoth = (uid) => !!(nominations[`${uid}_1`] && nominations[`${uid}_2`])
  const submittedAgentUids = agentUids.filter(hasBoth)
  const allSubmitted = agentUids.length > 0 && submittedAgentUids.length >= agentUids.length

  const dcStep = party?.dcStep || 'collect'
  const isHost     = role === 'host'
  const isDirector = role === 'director'
  const isAgent    = role === 'agent'

  // Agent casting picks (2 actors) — held locally until both are chosen, then
  // written together so "submitted" only flips once the full cast is in.
  const [castPicks, setCastPicks] = useState([])
  const [castSubmitting, setCastSubmitting] = useState(false)

  // Host auto-advance from collect → pick-start when all submitted
  const advancedRef = useRef(false)
  useEffect(() => {
    if (!isHost || dcStep !== 'collect' || !allSubmitted || advancedRef.current) return
    if (!party?.id) return
    advancedRef.current = true
    updateParty(party.id, { dcStep: 'pick-start', dcStepStartedAt: new Date() }).catch(() => {})
  }, [isHost, dcStep, allSubmitted, party?.id])
  // Reset advance ref on step change so the next auto-advance can fire
  useEffect(() => { advancedRef.current = false }, [dcStep])

  // ── COLLECT PHASE ────────────────────────────────────────────────────────
  if (dcStep === 'collect') {
    if (isAgent) {
      const my1 = nominations[`${currentUserId}_1`]
      const my2 = nominations[`${currentUserId}_2`]
      const alreadySubmitted = !!(my1 && my2)

      // Submitted — both actors are in (from Firestore on reconnect, or just-chosen
      // locally). Show the full cast + the waiting pulse.
      if (alreadySubmitted || castPicks.length === 2) {
        const cast = alreadySubmitted ? [my1, my2] : castPicks
        return (
          <div className="dc-casting dc-casting--agent-submitted">
            <p className="dc-casting__headline">Cast submitted!</p>
            <div className="dc-casting__my-cast">
              {cast.map((a, i) => (
                <div key={i} className="dc-casting__my-pick">
                  <img src={a.profile_path ? profileUrl(a.profile_path) : ACTOR_PH} alt={a.name} />
                  <p className="dc-casting__my-pick-name">{a.name}</p>
                </div>
              ))}
            </div>
            <p className="dc-casting__pulse">Waiting for other agents...</p>
          </div>
        )
      }

      // Selecting — first confirm picks actor 1, second picks actor 2, then both
      // nominations are written together.
      const handleCastPick = async (actor) => {
        if (castPicks.some((p) => p.id === actor.id)) return   // no duplicate actor
        const next = [...castPicks, actor]
        setCastPicks(next)
        if (next.length === 2 && !castSubmitting) {
          setCastSubmitting(true)
          await updateParty(party.id, {
            [`castingNominations.${currentUserId}_1`]: {
              actorId: next[0].id, name: next[0].name, profile_path: next[0].profile_path ?? null,
            },
            [`castingNominations.${currentUserId}_2`]: {
              actorId: next[1].id, name: next[1].name, profile_path: next[1].profile_path ?? null,
            },
          }).catch(() => {})
        }
      }

      return (
        <div className="dc-casting dc-casting--agent-pick">
          {castPicks.length === 1 && (
            <div className="dc-casting__cast-progress">
              <span className="dc-casting__cast-progress-label">Your cast</span>
              <div className="dc-casting__cast-progress-row">
                <div className="dc-casting__cast-chip">
                  <img
                    src={castPicks[0].profile_path ? profileUrl(castPicks[0].profile_path) : ACTOR_PH}
                    alt={castPicks[0].name}
                  />
                  <span>{castPicks[0].name}</span>
                </div>
                <div className="dc-casting__cast-chip dc-casting__cast-chip--empty" aria-hidden="true">
                  <span className="dc-casting__cast-placeholder">2</span>
                </div>
              </div>
            </div>
          )}
          <ActorPicker
            key={castPicks.length}
            slotRole="start"
            goalActor={null}
            blindPick
            headline={castPicks.length === 0 ? 'Pick your cast — actor 1 of 2' : 'Pick your cast — actor 2 of 2'}
            confirmLabel={castPicks.length === 0 ? 'Add Actor 1' : 'Add Actor 2'}
            onCancel={null}
            onConfirm={handleCastPick}
          />
        </div>
      )
    }
    // Director + Host: show submission status only
    return (
      <div className="dc-casting dc-casting--collecting">
        {isDirector && <DCDirectorBanner />}
        <h2 className="dc-casting__title">
          {isDirector ? 'Agents are pitching their clients...' : 'Casting Session'}
        </h2>
        {isHost && <DCHostDirectorLine party={party} />}
        <p className="dc-casting__sub">
          {submittedAgentUids.length}/{agentUids.length} submitted
        </p>
        <div className="dc-casting__avatars">
          {agentUids.map(uid => {
            const p = players[uid]
            const done = hasBoth(uid)
            const initial = (p?.username || '?').charAt(0).toUpperCase()
            return (
              <div key={uid} className="dc-casting__avatar-chip">
                <div className="dc-casting__avatar">
                  {p?.avatar
                    ? <img src={p.avatar} alt="" />
                    : <span className="party-avatar__initial">{initial}</span>}
                </div>
                <span className="dc-casting__avatar-name">
                  {done
                    ? <span className="party-ready-check" aria-label="Submitted"><IconCheck size={12} /></span>
                    : <span className="party-ready-dot" aria-label="Pitching" />}
                  <span>{p?.username || 'Guest'}</span>
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── PICK PHASE (start OR goal) — anonymous grid for Director ─────────────
  const isPickStart = dcStep === 'pick-start'
  const isPickGoal  = dcStep === 'pick-goal'

  if (isPickStart || isPickGoal) {
    // Dedupe: group nominations by actorId so each unique actor appears once.
    // Track ALL nominator UIDs internally so points go to every agent on a pick.
    const startActor = party?.startActor
    const startActorId = startActor?.id
    const groups = new Map() // actorId → { nomination, nominatorUids }
    for (const [key, n] of Object.entries(nominations)) {
      if (isPickGoal && startActorId && n.actorId === startActorId) continue
      const realUid = key.replace(/_[12]$/, '')   // strip the _1 / _2 nomination suffix
      const existing = groups.get(n.actorId)
      if (existing) {
        if (!existing.nominatorUids.includes(realUid)) existing.nominatorUids.push(realUid)
      } else {
        groups.set(n.actorId, { nomination: n, nominatorUids: [realUid] })
      }
    }
    const pool = Array.from(groups.values())

    const handlePick = async (group) => {
      if (!isDirector || !party?.id) return
      const { nomination, nominatorUids } = group
      const points = { ...(party.agentPoints || {}) }
      for (const u of nominatorUids) {
        points[u] = (points[u] || 0) + 2
      }
      const actorPayload = {
        id: nomination.actorId,
        name: nomination.name,
        profile_path: nomination.profile_path ?? null,
      }
      if (isPickStart) {
        await updateParty(party.id, {
          startActor: actorPayload,
          startNominatorUid: nominatorUids[0],
          startNominatorUids: nominatorUids,
          agentPoints: points,
          dcStep: 'pick-goal',
          dcStepStartedAt: new Date(),
        }).catch(() => {})
      } else {
        await updateParty(party.id, {
          goalActor: actorPayload,
          goalNominatorUid: nominatorUids[0],
          goalNominatorUids: nominatorUids,
          agentPoints: points,
          dcStep: 'done',
          status: 'reveal',
          dcStepStartedAt: new Date(),
        }).catch(() => {})
      }
    }

    return (
      <div className="dc-casting dc-casting--pick">
        {isDirector && <DCDirectorBanner />}
        <h2 className="dc-casting__title">
          {isDirector
            ? `Pick the ${isPickStart ? 'Start' : 'Goal'} Actor`
            : `Director is choosing the ${isPickStart ? 'Start' : 'Goal'}...`}
        </h2>
        {isHost && <DCHostDirectorLine party={party} />}
        <p className="dc-casting__sub">Anonymous — no names, no hints.</p>
        <div className="dc-casting__pool">
          {pool.map((group) => (
            <button
              type="button"
              key={group.nomination.actorId}
              className="dc-casting__pool-card"
              disabled={!isDirector}
              onClick={() => handlePick(group)}
              title={isDirector && group.nominatorUids.length > 1 ? `${group.nominatorUids.length} agents nominated this` : undefined}
            >
              <img
                src={group.nomination.profile_path ? profileUrl(group.nomination.profile_path) : ACTOR_PH}
                alt=""
              />
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ── DONE PHASE (transient, before reveal status takes over) ──────────────
  return (
    <div className="dc-casting dc-casting--done">
      <p className="dc-casting__pulse">Setting the stage...</p>
    </div>
  )
}
